import { useCallback } from 'react';
import { millisToSec, secToMillis } from 'utils/time';
import { useClipSelect } from '../../ClipSelectContext';

type SeekSyncProps = {
  suggestionId: number;
  /**
   * Eddy operates on the overall audio
   */
  eddySeek: (absoluteMillis: number) => void;
  /**
   * Preview operates on just the clip
   */
  previewSeek: (relativeSec: number) => void;
};

/**
 * synchronizes seek events between the preview and eddy players.  when one
 * player fires a seek event, the other player has to be updated to match that
 * time.
 *
 * both players use different times.  the preview iframe uses seconds and treats
 * the clip as the entire audio, with time 0 corresponding to suggestion.startMillis
 * in the overall audio.
 *
 * the eddy player uses milliseconds and uses absolute times corresponding to the
 * underlying full audio.
 */
export default function useSeekSync({
  suggestionId,
  eddySeek,
  previewSeek,
}: SeekSyncProps) {
  const { suggestion } = useClipSelect({ clipId: suggestionId });

  const handlePreviewSeek = useCallback(
    (relativeSec: number) => {
      eddySeek(secToMillis(relativeSec) + suggestion.startMillis);
    },
    [eddySeek, suggestion.startMillis],
  );

  const handleEddySeek = useCallback(
    (absoluteMillis: number) => {
      previewSeek(millisToSec(absoluteMillis - suggestion.startMillis));
    },
    [previewSeek, suggestion.startMillis],
  );

  return { handleEddySeek, handlePreviewSeek };
}
